<script>
  import { onMount } from 'svelte';
  import { workspace, callBbAi, refreshWorkspace } from '../lib/stores/workspace.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';
  import MultiValueField from './MultiValueField.svelte';
  // The one offer record behind a deal (HQ's map_offers): its six boxes are edited there, never copied here.
  import HqOffer from './HqOffer.svelte';

  let { kind, id, onClose } = $props();
  // The table is bb_deals; people see "offer" (Miles 2026-11-03: a deal is the offer).
  const word = (k) => (k === 'deal' ? 'offer' : k);

  let entity = $state(null);
  let loading = $state(true);
  let saving = $state(false);
  let error = $state(null);

  let pushbackField = $state(null);
  let pushbackResult = $state(null);
  let pushbackLoading = $state(false);

  let auditResult = $state(null);
  let auditLoading = $state(false);

  let pairLoading = $state(false);
  let discarding = $state(false);

  // v2.20b — Next picker for audience/deal
  let showAddNext = $state(false);
  let newNextSegmentKey = $state('');
  let nextLoading = $state(false);

  const outgoingConnections = $derived.by(() => {
    if (kind !== 'audience' && kind !== 'deal') return [];
    return (workspace.connections || [])
      .filter(c => c.from_segment_id === id && c.from_segment_kind === kind)
      .map(c => {
        const target =
          c.to_segment_kind === 'step' ? workspace.steps.find(s => s.id === c.to_segment_id) :
          c.to_segment_kind === 'deal' ? workspace.deals.find(d => d.id === c.to_segment_id) :
          c.to_segment_kind === 'audience' ? workspace.audiences.find(a => a.id === c.to_segment_id) : null;
        return {
          id: c.id,
          targetKind: c.to_segment_kind,
          targetName: target?.step_name || target?.name || '(unknown)',
          chain_id: c.chain_id
        };
      });
  });

  async function addNext() {
    if (!newNextSegmentKey) return;
    const [toKind, toId] = newNextSegmentKey.split(':');
    if (kind === 'deal' && toKind === 'audience') {
      showToast("Audience can't come after an offer", 'error');
      return;
    }
    if (toId === id) {
      showToast('Pick a different segment', 'error');
      return;
    }
    const existingChainConn = (workspace.connections || []).find(c =>
      (c.from_segment_id === id || c.to_segment_id === id) && c.chain_id
    );
    nextLoading = true;
    try {
      const { error } = await supabase.from('bb_segment_connections').insert({
        workspace_id: workspace.id,
        from_segment_id: id, from_segment_kind: kind,
        to_segment_id: toId, to_segment_kind: toKind,
        chain_id: existingChainConn?.chain_id || null,
        sort_order: 0
      });
      if (error) throw error;
      await refreshWorkspace();
      showToast('Next step added', 'success');
      showAddNext = false;
      newNextSegmentKey = '';
    } catch (e) {
      showToast('Add failed: ' + e.message, 'error');
    } finally {
      nextLoading = false;
    }
  }

  async function removeNext(connId) {
    if (!confirm('Remove this connection? The target segment stays.')) return;
    try {
      const { error } = await supabase.from('bb_segment_connections').delete().eq('id', connId);
      if (error) throw error;
      await refreshWorkspace();
      showToast('Connection removed', 'success');
    } catch (e) {
      showToast('Remove failed: ' + e.message, 'error');
    }
  }

  async function discardEntity() {
    if (!confirm(`Discard this ${word(kind)}? You can recover it from the database within 30 days.`)) return;
    discarding = true;
    try {
      const table = kind === 'audience' ? 'bb_audiences' : kind === 'deal' ? 'bb_deals' : 'bb_steps';
      const { error } = await supabase.from(table).update({ deleted_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
      await refreshWorkspace();
      showToast(`${word(kind).charAt(0).toUpperCase() + word(kind).slice(1)} discarded`, 'success');
      onClose();
    } catch (e) {
      showToast('Discard failed: ' + e.message, 'error');
    } finally {
      discarding = false;
    }
  }

  const AUDIENCE_FIELDS = [
    { key: 'name', label: 'Name', type: 'input' },
    { key: 'want', label: 'What they want', type: 'textarea' },
    { key: 'problem', label: 'Their problem', type: 'textarea' },
    { key: 'currently_doing', label: "What they're doing now", type: 'multi', help: 'Each alternative or behavior on its own line' },
    { key: 'cost_of_current', label: 'The cost of doing it that way', type: 'multi', help: 'What each is costing them' },
    { key: 'gap', label: "What's still missing", type: 'multi', help: 'What none of their current behaviors deliver' },
    { key: 'willing_to_give', label: "What they'd trade", type: 'multi', help: 'Time, money, attention, ego' }
  ];

  const DEAL_FIELDS = [
    { key: 'name', label: 'Name', type: 'input' },
    { key: 'what_it_is', label: 'What it is', type: 'textarea' },
    { key: 'how_it_works', label: 'How it works', type: 'textarea' },
    { key: 'result', label: 'Result', type: 'textarea' },
    { key: 'price', label: 'Price', type: 'input' },
    { key: 'real_cost', label: 'What the customer actually invests', type: 'multi', help: 'Non-money costs' }
  ];

  const STEP_FIELDS = [
    { key: 'step_name', label: 'Title', type: 'input' },
    { key: 'step_description', label: 'Description', type: 'textarea' },
    { key: 'purpose', label: 'Purpose', type: 'textarea' },
    { key: 'what_user_does', label: 'What user does', type: 'textarea' },
    { key: 'what_is_delivered', label: 'What is delivered', type: 'textarea' }
  ];

  const ENTRY_FIELDS = ['want', 'problem', 'currently_doing', 'cost_of_current', 'gap', 'willing_to_give'];
  const ENTRY_HINTS = {
    want: 'One per line',
    problem: 'One per line',
    currently_doing: 'Each alternative or behavior on its own line',
    cost_of_current: 'What each is costing them',
    gap: 'What none of their current behaviors deliver',
    willing_to_give: 'Money, time, attention, ego, behavior change'
  };

  function parseEntries(text) {
    if (!text) return [];
    return String(text).split('\n').map(l => l.trim()).filter(Boolean);
  }
  function joinEntries(entries) {
    return (entries || []).map(e => (e || '').trim()).filter(Boolean).join('\n');
  }

  let entryState = $state({});
  let lastInitId = $state(null);
  let entryLinks = $state([]);
  let linkPickerOpenFor = $state(null);
  let infoOpen = $state(false);
  let filterSet = $state(new Set());

  const LINK_COLOR = '#7AB8D4';

  function entryKey(fieldName, idx) { return `${fieldName}:${idx}`; }

  function autofocusEdit(node) {
    setTimeout(() => { try { node.focus(); node.select?.(); } catch {} }, 0);
    return {};
  }

  function entryLinkCount(fieldName, idx) {
    const k = entryKey(fieldName, idx);
    return entryLinks.reduce((n, l) => (l.from === k || l.to === k) ? n + 1 : n, 0);
  }

  function selectedSeed() {
    if (filterSet.size === 0) return null;
    return [...filterSet][0];
  }

  function treeRows() {
    const seed = selectedSeed();
    const reachable = filterVisibleSet;
    if (!seed || !reachable) return [];
    const rows = [];
    for (const f of ENTRY_FIELDS) {
      const arr = entryState[f]?.entries || [];
      const items = [];
      arr.forEach((text, idx) => {
        const k = entryKey(f, idx);
        if (reachable.has(k)) items.push({ idx, text, isSeed: k === seed, key: k });
      });
      if (items.length > 0) rows.push({ field: f, fieldLabel: f.replace(/_/g, ' '), items });
    }
    return rows;
  }

  function toggleFilter(fieldName, idx, shiftKey) {
    const k = entryKey(fieldName, idx);
    if (shiftKey) {
      const next = new Set(filterSet);
      if (next.has(k)) next.delete(k); else next.add(k);
      filterSet = next;
    } else {
      if (filterSet.size === 1 && filterSet.has(k)) filterSet = new Set();
      else filterSet = new Set([k]);
    }
  }

  function clearFilter() {
    filterSet = new Set();
  }

  function directNeighbors(seeds) {
    const visited = new Set(seeds);
    for (const cur of seeds) {
      for (const link of entryLinks) {
        if (link.from === cur) visited.add(link.to);
        if (link.to === cur) visited.add(link.from);
      }
    }
    return visited;
  }

  const filterVisibleSet = $derived.by(() => {
    if (filterSet.size === 0) return null;
    return directNeighbors(filterSet);
  });

  function isVisibleInFilter(fieldName, idx) {
    if (!filterVisibleSet) return true;
    return filterVisibleSet.has(entryKey(fieldName, idx));
  }

  async function saveLinks() {
    if (!entity?.id) return;
    await supabase.from('bb_audiences').update({ entry_links: entryLinks }).eq('id', entity.id);
    entity.entry_links = entryLinks;
  }

  function openLinkPicker(fieldName, idx) {
    linkPickerOpenFor = entryKey(fieldName, idx);
  }
  function closeLinkPicker() {
    linkPickerOpenFor = null;
  }

  async function createLinkFromPicker(fromKey, toKey) {
    const [aField] = fromKey.split(':');
    const [bField] = toKey.split(':');
    const aPos = ENTRY_FIELDS.indexOf(aField);
    const bPos = ENTRY_FIELDS.indexOf(bField);
    const link = aPos < bPos ? { from: fromKey, to: toKey } : { from: toKey, to: fromKey };
    const exists = entryLinks.some(l => l.from === link.from && l.to === link.to);
    if (!exists) {
      entryLinks = [...entryLinks, link];
      await saveLinks();
    }
  }

  async function removeLink(fieldName, idx, otherKey) {
    const k = entryKey(fieldName, idx);
    entryLinks = entryLinks.filter(l => !(
      (l.from === k && l.to === otherKey) || (l.to === k && l.from === otherKey)
    ));
    await saveLinks();
  }

  function linksTouching(fromKey) {
    return entryLinks.filter(l => l.from === fromKey || l.to === fromKey).map(l => {
      const otherKey = l.from === fromKey ? l.to : l.from;
      const [otherField, otherIdxStr] = otherKey.split(':');
      const otherIdx = parseInt(otherIdxStr);
      const otherText = entryState[otherField]?.entries?.[otherIdx] || '(removed)';
      return { otherKey, otherField, otherText };
    });
  }

  function initEntryState() {
    const next = {};
    for (const f of ENTRY_FIELDS) {
      next[f] = {
        entries: parseEntries(entity?.[f] || ''),
        draft: '',
        editingIdx: null,
        editingDraft: ''
      };
    }
    entryState = next;
  }

  $effect(() => {
    if (kind === 'audience' && entity?.id && entity.id !== lastInitId) {
      lastInitId = entity.id;
      initEntryState();
    }
  });

  function addEntry(fieldName) {
    const draft = (entryState[fieldName]?.draft || '').trim();
    if (!draft) return;
    entryState[fieldName].entries = [...entryState[fieldName].entries, draft];
    entryState[fieldName].draft = '';
    entity[fieldName] = joinEntries(entryState[fieldName].entries);
    saveField(fieldName);
  }

  function startEntryEdit(fieldName, idx) {
    entryState[fieldName].editingIdx = idx;
    entryState[fieldName].editingDraft = entryState[fieldName].entries[idx] || '';
  }

  function commitEntryEdit(fieldName) {
    const s = entryState[fieldName];
    if (!s || s.editingIdx === null) return;
    const next = (s.editingDraft || '').trim();
    if (!next) {
      deleteEntry(fieldName, s.editingIdx);
      return;
    }
    entryState[fieldName].entries[s.editingIdx] = next;
    entryState[fieldName].entries = [...entryState[fieldName].entries];
    entryState[fieldName].editingIdx = null;
    entryState[fieldName].editingDraft = '';
    entity[fieldName] = joinEntries(entryState[fieldName].entries);
    saveField(fieldName);
  }

  function cancelEntryEdit(fieldName) {
    entryState[fieldName].editingIdx = null;
    entryState[fieldName].editingDraft = '';
  }

  function deleteEntry(fieldName, idx) {
    const key = entryKey(fieldName, idx);
    entryLinks = entryLinks
      .filter(l => l.from !== key && l.to !== key)
      .map(l => {
        const newL = { ...l };
        const [ff, fi] = l.from.split(':');
        const [tf, ti] = l.to.split(':');
        if (ff === fieldName && parseInt(fi) > idx) newL.from = `${ff}:${parseInt(fi) - 1}`;
        if (tf === fieldName && parseInt(ti) > idx) newL.to = `${tf}:${parseInt(ti) - 1}`;
        return newL;
      });
    entryState[fieldName].entries = entryState[fieldName].entries.filter((_, i) => i !== idx);
    entryState[fieldName].editingIdx = null;
    entryState[fieldName].editingDraft = '';
    entity[fieldName] = joinEntries(entryState[fieldName].entries);
    saveField(fieldName);
    saveLinks();
  }

  function onDraftKey(e, fieldName) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addEntry(fieldName);
    }
  }

  function onEditKey(e, fieldName) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitEntryEdit(fieldName);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEntryEdit(fieldName);
    }
  }

  const fields = $derived(
    kind === 'audience' ? AUDIENCE_FIELDS :
    // A linked offer is edited in its HQ boxes (HqOffer), not in the builder's old deal fields.
    kind === 'deal' ? (entity?.offer_id ? [] : DEAL_FIELDS) :
    STEP_FIELDS
  );
  const tableName = $derived(
    kind === 'audience' ? 'bb_audiences' :
    kind === 'deal' ? 'bb_deals' :
    'bb_steps'
  );
  const otherKind = $derived(kind === 'audience' ? 'deal' : 'audience');
  const otherList = $derived(kind === 'audience' ? workspace.deals : workspace.audiences);
  const isPairable = $derived(kind === 'audience' || kind === 'deal');

  onMount(async () => {
    await loadEntity();
  });

  async function loadEntity() {
    loading = true;
    const { data, error: e } = await supabase.from(tableName).select('*').eq('id', id).maybeSingle();
    if (e || !data) {
      error = e?.message || 'Entity not found';
    } else {
      entity = data;
      if (kind === 'audience') {
        initEntryState();
        entryLinks = Array.isArray(entity.entry_links) ? [...entity.entry_links] : [];
        linkPickerOpenFor = null;
        lastInitId = entity.id;
      }
    }
    loading = false;
  }

  async function saveField(key) {
    if (!entity) return;
    saving = true; error = null;
    const { error: e } = await supabase.from(tableName).update({ [key]: entity[key] }).eq('id', id);
    if (e) error = e.message;
    saving = false;
    await refreshWorkspace();
  }

  async function runPushback(fieldKey) {
    pushbackField = fieldKey;
    pushbackResult = null;
    pushbackLoading = true;
    try {
      const result = await callBbAi('pushback_field', {
        entity_kind: kind,
        entity_data: entity,
        field_name: fieldKey,
        current_value: entity[fieldKey] || ''
      });
      pushbackResult = result;
    } catch (e) {
      pushbackResult = { critique: 'Pushback failed: ' + e.message };
    } finally {
      pushbackLoading = false;
    }
  }

  function applyPushback() {
    if (pushbackResult?.replacement && pushbackField) {
      entity[pushbackField] = pushbackResult.replacement;
      saveField(pushbackField);
    }
    pushbackResult = null;
    pushbackField = null;
  }

  function dismissPushback() {
    pushbackResult = null;
    pushbackField = null;
  }

  async function runAudit() {
    auditResult = null;
    auditLoading = true;
    try {
      const result = await callBbAi('audit', { scope: kind, scope_id: id });
      auditResult = result;
    } catch (e) {
      auditResult = { summary: 'Audit failed: ' + e.message, findings: [] };
    } finally {
      auditLoading = false;
    }
  }

  function openChainBuilder() {
    window.dispatchEvent(new CustomEvent('open-chain-builder', {
      detail: kind === 'audience' ? { audienceId: id } : { dealId: id }
    }));
    onClose();
  }

  async function createCustomPair() {
    pairLoading = true;
    try {
      const payload = { workspace_id: workspace.id };
      if (kind === 'audience') payload.audience_id = id;
      else payload.deal_id = id;
      const r = await callBbAi('pair_segment', payload);
      if (r?.error) throw new Error(r.detail?.message || r.error);
      await refreshWorkspace();
      showToast(`Custom ${kind === 'audience' ? 'offer' : 'audience'} created: "${r.name}"`, 'success');
    } catch (e) {
      showToast('Generate failed: ' + e.message, 'error');
    } finally {
      pairLoading = false;
    }
  }

  function fieldConfidence(key) {
    return entity?.field_confidence?.[key];
  }
</script>

<div class="ee-overlay" onclick={onClose}>
  <div class="ee-modal" onclick={(e) => e.stopPropagation()}>
    <div class="ee-header">
      <div>
        <div class="ee-kind-label">{word(kind)}</div>
        <h3>{entity?.name || entity?.step_name || (loading ? 'Loading…' : 'Untitled')}</h3>
      </div>
      {#if kind === 'audience'}
        <button class="ee-info-btn" onclick={() => infoOpen = !infoOpen} type="button" title="How this works">i</button>
      {/if}
      <button class="ee-close" onclick={onClose} type="button">×</button>
    </div>

    {#if loading}
      <div class="ee-loading">Loading…</div>
    {:else if error}
      <div class="ee-error">{error}</div>
    {:else if entity}
      <div class="ee-actions-row">
        <button class="ee-action" onclick={runAudit} disabled={auditLoading} type="button">
          {auditLoading ? 'Auditing…' : '🔎 Audit'}
        </button>
        {#if isPairable}
          <button class="ee-action" onclick={openChainBuilder} type="button">⚡ Build chain</button>
          <button class="ee-action ee-action-secondary" onclick={createCustomPair} disabled={pairLoading} type="button">
            {pairLoading ? 'Generating…' : `Create custom ${kind === 'audience' ? 'offer' : 'audience'}`}
          </button>
        {/if}
        <!-- A company's offer is retired in HQ, never discarded here (it would come straight back). -->
        {#if !(kind === 'deal' && entity.offer_id && workspace.partner_id)}
          <button class="ee-action ee-discard" onclick={discardEntity} disabled={discarding} type="button">
            {discarding ? 'Discarding…' : '🗑 Discard'}
          </button>
        {/if}
      </div>

      {#if auditResult}
        <div class="ee-audit-card">
          <div class="ee-audit-summary">{auditResult.summary}</div>
          {#if auditResult.findings?.length}
            <ul class="ee-findings">
              {#each auditResult.findings as f}
                <li class="ee-finding ee-{f.severity}">
                  <span class="ee-sev">{f.severity}</span>
                  <div>
                    <div class="ee-finding-text"><strong>{f.area}:</strong> {f.finding}</div>
                    {#if f.suggestion}<div class="ee-suggest">{f.suggestion}</div>{/if}
                  </div>
                </li>
              {/each}
            </ul>
          {/if}
          <button class="ee-secondary" onclick={() => auditResult = null} type="button">Close audit</button>
        </div>
      {/if}

      {#if kind === 'audience' && selectedSeed() && treeRows().length > 0}
        {@const seedKey = selectedSeed()}
        {@const seedParts = seedKey.split(':')}
        {@const seedField = seedParts[0]}
        {@const seedIdx = parseInt(seedParts[1])}
        {@const seedText = entryState[seedField]?.entries?.[seedIdx] || '(removed)'}
        <div class="ee-tree-popup">
          <div class="ee-tree-head">
            <span class="ee-tree-title">Connections for "<strong>{seedText}</strong>"</span>
            <button class="ee-tree-close" onclick={clearFilter} type="button">✕</button>
          </div>
          <div class="ee-tree-body">
            {#each treeRows() as row}
              <div class="ee-tree-section">
                <div class="ee-tree-section-label">{row.fieldLabel}</div>
                {#each row.items as it}
                  <div class="ee-tree-item" class:seed={it.isSeed}>
                    <span class="ee-tree-bullet"></span>
                    <span class="ee-tree-item-text">{it.text}</span>
                    {#if it.isSeed}<span class="ee-tree-seed-tag">selected</span>{/if}
                  </div>
                {/each}
              </div>
            {/each}
          </div>
        </div>
      {/if}

      {#if kind === 'audience'}
        {#if infoOpen}
          <div class="ee-info-popup">
            <h4>How to fill this out</h4>
            <ul>
              <li><strong>Add</strong>: type in the bottom box of any section, press Enter.</li>
              <li><strong>Edit</strong>: click any entry to change it. Enter to save, Esc to cancel.</li>
              <li><strong>Delete</strong>: click the × on the right of an entry.</li>
              <li><strong>Connect</strong>: click 🔗 on an entry, then pick another entry from a different section.</li>
              <li><strong>Unlink</strong>: open 🔗 again on the same entry and click the ✓ on the linked target.</li>
              <li><strong>Filter</strong>: click a colored stripe on an entry to focus on its connected path. Shift+click stripes to extend. Click empty space to clear.</li>
            </ul>
            <p class="ee-info-tip">Each connection gets its own color and shows up as a stripe on both linked entries.</p>
            <button class="ee-info-close" onclick={() => infoOpen = false} type="button">Got it</button>
          </div>
        {/if}
      {/if}

      {#if kind === 'deal' && entity}
        <HqOffer deal={entity} onLinked={(oid) => { entity = { ...entity, offer_id: oid }; }} />
      {/if}

      <div class="ee-fields" onclick={(e) => { if (e.target === e.currentTarget) clearFilter(); }}>
        {#each fields as f}
          <div class="ee-field">
            <div class="ee-field-head">
              <label>
                {f.label}
                {#if fieldConfidence(f.key)}
                  <span class="ee-conf ee-conf-{fieldConfidence(f.key)}">{fieldConfidence(f.key)}</span>
                {/if}
              </label>
              <button class="ee-pushback-btn" onclick={() => runPushback(f.key)} disabled={pushbackLoading && pushbackField === f.key} type="button">
                {pushbackLoading && pushbackField === f.key ? '…' : 'Push back'}
              </button>
            </div>
            {#if kind === 'audience' && ENTRY_FIELDS.includes(f.key) && entryState[f.key]}
              {@const es = entryState[f.key] || { entries: [], draft: '', editingIdx: null, editingDraft: '' }}
              {#if ENTRY_HINTS[f.key]}<p class="ee-section-hint">{ENTRY_HINTS[f.key]}</p>{/if}
              {#if es.entries.length > 0}
                <div class="ee-entry-list">
                  {#each es.entries as entry, idx (idx)}
                    {@const k = entryKey(f.key, idx)}
                    {@const linkCount = entryLinkCount(f.key, idx)}
                    {@const visible = isVisibleInFilter(f.key, idx)}
                    {@const isSeed = filterSet.has(k)}
                    {#if es.editingIdx === idx}
                      <div class="ee-entry-row editing">
                        <input
                          class="ee-entry-input"
                          bind:value={entryState[f.key].editingDraft}
                          onkeydown={(e) => onEditKey(e, f.key)}
                          onblur={() => commitEntryEdit(f.key)}
                          use:autofocusEdit
                        />
                      </div>
                    {:else}
                      <div class="ee-entry-wrap">
                        <div class="ee-entry-row" class:filter-dimmed={!visible} class:seed={isSeed}>
                          {#if linkCount > 0}
                            <button
                              class="ee-link-stripe"
                              class:active={isSeed}
                              onclick={(e) => { e.stopPropagation(); toggleFilter(f.key, idx, e.shiftKey); }}
                              title="{linkCount} connection{linkCount === 1 ? '' : 's'} — click to view tree"
                              type="button"
                            >
                              {#each Array(Math.min(linkCount, 4)) as _}
                                <span class="ee-stripe-tick"></span>
                              {/each}
                            </button>
                          {/if}
                          <span class="ee-entry-bullet">•</span>
                          <button class="ee-entry-text" onclick={() => startEntryEdit(f.key, idx)} type="button">{entry}</button>
                          <button class="ee-entry-link" onclick={() => openLinkPicker(f.key, idx)} type="button" title="Connect to another entry">🔗</button>
                          <button class="ee-entry-delete" onclick={() => deleteEntry(f.key, idx)} type="button" title="Delete">✕</button>
                        </div>
                        {#if linkPickerOpenFor === k}
                          <div class="ee-link-picker" onclick={(e) => e.stopPropagation()}>
                            <div class="ee-link-picker-header">
                              Connect "<strong>{entry}</strong>" to…
                              <button class="ee-link-picker-close" onclick={closeLinkPicker} type="button">✕</button>
                            </div>
                            {#if ENTRY_FIELDS.every(f2 => f2 === f.key || (entryState[f2]?.entries || []).length === 0)}
                              <p class="ee-link-picker-empty">Add entries to other sections first, then come back here to connect them.</p>
                            {:else}
                              <div class="ee-link-picker-body">
                                {#each ENTRY_FIELDS as f2}
                                  {#if f2 !== f.key && (entryState[f2]?.entries || []).length > 0}
                                    <div class="ee-link-picker-group">
                                      <div class="ee-link-picker-group-label">{f2.replace(/_/g, ' ')}</div>
                                      {#each entryState[f2].entries as et2, idx2}
                                        {@const k2 = entryKey(f2, idx2)}
                                        {@const isLinked = entryLinks.some(l => (l.from === k && l.to === k2) || (l.to === k && l.from === k2))}
                                        <button
                                          class="ee-link-picker-item"
                                          class:already-linked={isLinked}
                                          onclick={() => isLinked ? removeLink(f.key, idx, k2) : createLinkFromPicker(k, k2)}
                                          type="button"
                                        >
                                          <span class="ee-link-picker-mark">{isLinked ? '✓' : '+'}</span>
                                          <span class="ee-link-picker-text">{et2}</span>
                                        </button>
                                      {/each}
                                    </div>
                                  {/if}
                                {/each}
                              </div>
                            {/if}
                          </div>
                        {/if}
                      </div>
                    {/if}
                  {/each}
                </div>
              {/if}
              <div class="ee-entry-add">
                <input
                  class="ee-entry-input"
                  data-entry-draft={f.key}
                  bind:value={entryState[f.key].draft}
                  onkeydown={(e) => onDraftKey(e, f.key)}
                  placeholder="Add an entry…"
                />
                <button
                  class="ee-entry-add-btn"
                  onclick={() => addEntry(f.key)}
                  disabled={!(entryState[f.key]?.draft || '').trim()}
                  type="button"
                >Add</button>
              </div>
            {:else if f.type === 'multi'}
              <MultiValueField bind:value={entity[f.key]} help={f.help} placeholder="Add an item…" />
              <button class="ee-save-field" onclick={() => saveField(f.key)} disabled={saving} type="button">Save</button>
            {:else if f.type === 'textarea'}
              <textarea bind:value={entity[f.key]} rows="2" onblur={() => saveField(f.key)}></textarea>
            {:else}
              <input bind:value={entity[f.key]} onblur={() => saveField(f.key)} />
            {/if}

            {#if pushbackField === f.key && pushbackResult}
              <div class="ee-pushback-card">
                <div class="ee-pb-critique">{pushbackResult.critique}</div>
                {#if pushbackResult.replacement}
                  <div class="ee-pb-replacement">
                    <div class="ee-pb-label">Drop-in replacement:</div>
                    <div class="ee-pb-text">{pushbackResult.replacement}</div>
                  </div>
                  <div class="ee-pb-actions">
                    <button class="ee-primary" onclick={applyPushback} type="button">Apply</button>
                    <button class="ee-secondary" onclick={dismissPushback} type="button">Dismiss</button>
                  </div>
                {:else if pushbackResult.questions?.length}
                  <ul class="ee-pb-questions">
                    {#each pushbackResult.questions as q}
                      <li>{q}</li>
                    {/each}
                  </ul>
                  <button class="ee-secondary" onclick={dismissPushback} type="button">Got it</button>
                {:else}
                  <button class="ee-secondary" onclick={dismissPushback} type="button">Dismiss</button>
                {/if}
              </div>
            {/if}
          </div>
        {/each}

        {#if kind === 'audience' || kind === 'deal'}
          <section class="ee-section">
            <h3>Next <span class="ee-section-hint">— what comes after this {word(kind)}</span></h3>
            {#each outgoingConnections as cn (cn.id)}
              <div class="ee-next-row">
                <span class="ee-next-arrow">→</span>
                <span class="ee-next-kind">{word(cn.targetKind)}</span>
                <span class="ee-next-name">{cn.targetName}</span>
                <button class="ee-row-discard" onclick={() => removeNext(cn.id)} type="button" title="Remove connection">✕</button>
              </div>
            {/each}
            {#if !showAddNext}
              <button class="ee-add-row" onclick={() => showAddNext = true} type="button">+ Add next</button>
            {:else}
              <div class="ee-add-next">
                <select bind:value={newNextSegmentKey}>
                  <option value="">— Pick a segment —</option>
                  <optgroup label="Steps">
                    {#each workspace.steps.filter(s => !s.deleted_at) as s}
                      <option value="step:{s.id}">{s.step_name || 'Untitled step'}</option>
                    {/each}
                  </optgroup>
                  <optgroup label="Offers">
                    {#each workspace.deals.filter(d => !d.deleted_at && d.id !== id) as d}
                      <option value="deal:{d.id}">{d.name}</option>
                    {/each}
                  </optgroup>
                  {#if kind === 'audience'}
                    <optgroup label="Audiences">
                      {#each workspace.audiences.filter(a => !a.deleted_at && a.id !== id) as a}
                        <option value="audience:{a.id}">{a.name}</option>
                      {/each}
                    </optgroup>
                  {/if}
                </select>
                <button class="ee-confirm-next" onclick={addNext} disabled={!newNextSegmentKey || nextLoading} type="button">{nextLoading ? 'Adding…' : 'Add'}</button>
                <button class="ee-cancel-next" onclick={() => { showAddNext = false; newNextSegmentKey = ''; }} type="button">Cancel</button>
              </div>
            {/if}
          </section>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .ee-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 900;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .ee-modal {
    background: linear-gradient(180deg, rgba(20,18,22,0.96), rgba(12,11,14,0.96));
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 18px;
    width: 100%;
    max-width: 720px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05);
    animation: modalIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
  }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .ee-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 22px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .ee-kind-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    color: #d4af37;
    margin-bottom: 4px;
  }
  .ee-header h3 {
    font-family: 'DM Serif Display', serif;
    font-size: 24px;
    font-weight: 400;
    margin: 0;
    color: #f0efe9;
    letter-spacing: -0.01em;
  }
  .ee-close {
    background: transparent; border: none;
    color: #888; font-size: 24px; cursor: pointer;
    padding: 0 8px; line-height: 1;
    margin: 0; box-shadow: none;
  }
  .ee-close:hover { color: #fff; transform: none; background: transparent; }
  .ee-loading, .ee-error {
    padding: 40px; text-align: center; color: #888;
  }
  .ee-error { color: #d48484; }
  .ee-actions-row {
    display: flex; gap: 8px; padding: 14px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    flex-wrap: wrap;
  }
  .ee-action {
    background: rgba(212,175,55,0.1);
    border: 1px solid rgba(212,175,55,0.25);
    color: #d4af37;
    padding: 8px 16px;
    border-radius: 999px;
    font-size: 12px;
    cursor: pointer;
    margin: 0;
    box-shadow: none;
    font-weight: 500;
  }
  .ee-action:hover:not(:disabled) {
    background: rgba(212,175,55,0.2);
    transform: none;
  }
  .ee-action:disabled { opacity: 0.4; }
  .ee-discard {
    background: rgba(220,100,100,0.1);
    color: #d48484;
    border: 1px solid rgba(220,100,100,0.25);
  }
  .ee-discard:hover:not(:disabled) {
    background: rgba(220,100,100,0.2);
  }
  .ee-pair-form {
    padding: 12px 24px;
    background: rgba(212,175,55,0.05);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
  }
  .ee-pair-form label { color: #d8d8dc; font-size: 12px; }
  .ee-pair-form select {
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 13px;
    margin: 0;
  }
  .ee-audit-card {
    margin: 16px 24px;
    padding: 16px;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
  }
  .ee-audit-summary {
    color: #f0efe9;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 12px;
    font-style: italic;
  }
  .ee-findings { list-style: none; padding: 0; margin: 0 0 12px; display: flex; flex-direction: column; gap: 8px; }
  .ee-finding {
    display: flex; gap: 10px; align-items: flex-start;
    padding: 10px 12px;
    background: rgba(255,255,255,0.02);
    border-left: 3px solid;
    border-radius: 6px;
  }
  .ee-red { border-left-color: #d48484; }
  .ee-amber { border-left-color: #d4af37; }
  .ee-green { border-left-color: #7cd992; }
  .ee-sev {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    padding: 2px 8px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .ee-red .ee-sev { background: rgba(212,100,100,0.2); color: #d48484; }
  .ee-amber .ee-sev { background: rgba(212,175,55,0.2); color: #d4af37; }
  .ee-green .ee-sev { background: rgba(124,217,146,0.2); color: #7cd992; }
  .ee-finding-text { color: #d8d8dc; font-size: 13px; line-height: 1.5; }
  .ee-finding-text strong { color: #f0efe9; }
  .ee-suggest {
    color: #9aa0a6; font-size: 12px; margin-top: 4px; font-style: italic;
  }
  .ee-fields {
    flex: 1;
    overflow-y: auto;
    padding: 16px 24px 24px;
  }
  .ee-field { margin-bottom: 18px; }
  .ee-field-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 6px;
  }
  .ee-field-head label {
    color: #d8d8dc;
    font-size: 12px;
    font-weight: 500;
    display: flex; align-items: center; gap: 8px;
  }
  .ee-conf {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    padding: 1px 6px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .ee-conf-high { background: rgba(124,217,146,0.18); color: #7cd992; }
  .ee-conf-medium { background: rgba(212,175,55,0.18); color: #d4af37; }
  .ee-conf-low { background: rgba(212,100,100,0.18); color: #d48484; }
  .ee-pushback-btn {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    color: #9aa0a6;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    cursor: pointer;
    margin: 0;
    box-shadow: none;
  }
  .ee-pushback-btn:hover:not(:disabled) {
    color: #d4af37;
    border-color: rgba(212,175,55,0.4);
    transform: none;
  }
  .ee-pushback-btn:disabled { opacity: 0.4; }
  .ee-fields textarea, .ee-fields input {
    width: 100%;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.08);
    color: #f4f4f6;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13.5px;
    box-sizing: border-box;
    font-family: inherit;
    line-height: 1.5;
    margin: 0;
  }
  .ee-fields textarea:focus, .ee-fields input:focus {
    outline: none;
    border-color: rgba(212,175,55,0.4);
  }
  .ee-save-field {
    margin-top: 4px;
    background: rgba(212,175,55,0.12);
    color: #d4af37;
    border: 1px solid rgba(212,175,55,0.3);
    padding: 4px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    box-shadow: none;
  }
  .ee-save-field:hover:not(:disabled) { background: rgba(212,175,55,0.2); transform: none; }
  .ee-save-field:disabled { opacity: 0.4; }
  .ee-pushback-card {
    margin-top: 8px;
    padding: 12px 14px;
    background: rgba(212,175,55,0.06);
    border: 1px solid rgba(212,175,55,0.2);
    border-radius: 8px;
  }
  .ee-pb-critique {
    color: #f0efe9;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 10px;
    font-style: italic;
  }
  .ee-pb-replacement {
    background: rgba(0,0,0,0.3);
    padding: 10px 12px;
    border-radius: 6px;
    margin-bottom: 10px;
  }
  .ee-pb-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #d4af37;
    margin-bottom: 4px;
  }
  .ee-pb-text {
    color: #f0efe9;
    font-size: 13px;
    line-height: 1.5;
  }
  .ee-pb-actions { display: flex; gap: 6px; }
  .ee-pb-questions {
    list-style: disc; padding-left: 20px; color: #d8d8dc; font-size: 12px;
    margin: 0 0 10px;
  }
  .ee-pb-questions li { margin-bottom: 4px; line-height: 1.5; }
  .ee-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c; border: none;
    padding: 8px 16px; border-radius: 6px; cursor: pointer;
    font-weight: 600; font-size: 12px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
    margin: 0;
  }
  .ee-secondary {
    background: rgba(255,255,255,0.04);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 8px 16px; border-radius: 6px; cursor: pointer;
    font-size: 12px;
    margin: 0; box-shadow: none;
  }

  /* v2.20b — Next picker */
  .ee-section { margin-top: 18px; padding-top: 14px; border-top: 1px solid rgba(255,255,255,0.06); }
  .ee-section h3 {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; text-transform: uppercase; letter-spacing: 1.4px;
    color: #9aa0a6; margin: 0 0 10px; font-weight: 500;
  }
  .ee-section-hint { color: #6a6a6e; font-weight: 400; text-transform: none; letter-spacing: 0.4px; }

  .ee-next-row {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 8px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    margin-bottom: 4px;
  }
  .ee-next-arrow { color: #d4af37; font-weight: 600; }
  .ee-next-kind { font-family: 'JetBrains Mono', monospace; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #9aa0a6; }
  .ee-next-name { flex: 1; color: #f0efe9; font-size: 13px; }
  .ee-row-discard {
    background: rgba(255,255,255,0.04);
    color: #9aa0a6;
    border: 1px solid rgba(255,255,255,0.06);
    width: 22px; height: 22px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 11px; line-height: 1; padding: 0; margin: 0;
  }
  .ee-row-discard:hover {
    background: rgba(220,100,100,0.15);
    color: #d48484;
    border-color: rgba(220,100,100,0.3);
  }

  .ee-add-row {
    background: rgba(212,175,55,0.08);
    color: #d4af37;
    border: 1px dashed rgba(212,175,55,0.3);
    padding: 7px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    width: 100%;
    margin: 0;
  }
  .ee-add-row:hover:not(:disabled) {
    background: rgba(212,175,55,0.15);
    border-style: solid;
  }

  .ee-add-next {
    display: flex; gap: 6px; align-items: center;
    padding: 8px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
  }
  .ee-add-next select {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 6px 8px;
    border-radius: 6px;
    font-size: 12px;
    margin: 0;
    font-family: inherit;
  }
  .ee-confirm-next {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 11px;
    margin: 0;
  }
  .ee-confirm-next:disabled { opacity: 0.4; cursor: not-allowed; }
  .ee-cancel-next {
    background: rgba(255,255,255,0.05);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    margin: 0;
  }
  .ee-section-hint {
    color: #6a6a6e;
    font-size: 11px;
    margin: 2px 0 8px;
  }
  .ee-entry-list {
    display: flex; flex-direction: column; gap: 4px;
    margin-bottom: 6px;
  }
  .ee-entry-row {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 10px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 6px;
  }
  .ee-entry-row.editing {
    background: rgba(212,175,55,0.06);
    border-color: rgba(212,175,55,0.35);
  }
  .ee-entry-bullet {
    color: #6a6a6e;
    font-size: 12px;
  }
  .ee-entry-text {
    flex: 1;
    background: none;
    border: none;
    color: #d8d8dc;
    text-align: left;
    cursor: text;
    font-size: 13px;
    padding: 0;
    font-family: inherit;
  }
  .ee-entry-text:hover { color: #f0efe9; }
  .ee-entry-delete {
    background: rgba(255,255,255,0.04);
    color: #9aa0a6;
    border: 1px solid rgba(255,255,255,0.08);
    width: 20px; height: 20px; border-radius: 50%;
    cursor: pointer; font-size: 10px; line-height: 1; padding: 0;
    flex-shrink: 0;
  }
  .ee-entry-delete:hover {
    background: rgba(220,100,100,0.15);
    color: #d48484;
    border-color: rgba(220,100,100,0.3);
  }
  .ee-entry-add {
    display: flex; gap: 6px; margin-top: 4px;
  }
  .ee-entry-input {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 7px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
  }
  .ee-entry-input:focus {
    outline: none;
    border-color: rgba(212,175,55,0.4);
  }
  .ee-entry-add-btn {
    background: rgba(212,175,55,0.12);
    color: #d4af37;
    border: 1px solid rgba(212,175,55,0.3);
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.8px;
  }
  .ee-entry-add-btn:hover:not(:disabled) {
    background: rgba(212,175,55,0.2);
    border-color: rgba(212,175,55,0.5);
  }
  .ee-entry-add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .ee-entry-wrap {
    display: flex; flex-direction: column; gap: 4px;
    position: relative;
  }
  .ee-entry-link {
    background: rgba(212,175,55,0.08);
    color: #d4af37;
    border: 1px solid rgba(212,175,55,0.25);
    width: 24px; height: 24px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    padding: 0;
    flex-shrink: 0;
  }
  .ee-entry-link:hover {
    background: rgba(212,175,55,0.2);
    border-color: rgba(212,175,55,0.5);
  }
  .ee-entry-row {
    position: relative;
    transition: opacity 0.15s ease;
  }
  .ee-entry-row.filter-dimmed {
    opacity: 0.18;
    pointer-events: none;
  }
  .ee-entry-row.seed {
    background: rgba(122,184,212,0.12);
    border-color: rgba(122,184,212,0.5);
    box-shadow: 0 0 0 1px rgba(122,184,212,0.25);
  }
  .ee-link-stripe {
    display: flex; flex-direction: column;
    gap: 2px;
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    flex-shrink: 0;
  }
  .ee-link-stripe .ee-stripe-tick {
    display: block;
    width: 4px;
    height: 6px;
    border-radius: 2px;
    background: #7AB8D4;
    box-shadow: 0 0 4px rgba(122,184,212,0.5);
  }
  .ee-link-stripe:hover .ee-stripe-tick {
    background: #9ec8de;
    box-shadow: 0 0 8px rgba(122,184,212,0.8);
  }
  .ee-link-stripe.active .ee-stripe-tick {
    background: #b8dbed;
    box-shadow: 0 0 10px rgba(122,184,212,0.9);
  }
  .ee-tree-popup {
    position: sticky;
    top: 0;
    background: rgba(15,18,22,0.96);
    border: 1px solid rgba(122,184,212,0.4);
    border-radius: 12px;
    margin: 0 0 14px;
    z-index: 6;
    box-shadow: 0 12px 30px rgba(0,0,0,0.55), 0 0 24px rgba(122,184,212,0.15);
  }
  .ee-tree-head {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(122,184,212,0.2);
  }
  .ee-tree-title { color: #d8d8dc; font-size: 12px; }
  .ee-tree-title strong { color: #7AB8D4; font-weight: 500; }
  .ee-tree-close {
    background: rgba(255,255,255,0.06);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 22px; height: 22px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    padding: 0;
  }
  .ee-tree-close:hover { background: rgba(122,184,212,0.15); color: #7AB8D4; }
  .ee-tree-body {
    padding: 10px 14px;
    max-height: 360px;
    overflow-y: auto;
    display: flex; flex-direction: column; gap: 10px;
  }
  .ee-tree-section {
    border-left: 2px solid rgba(122,184,212,0.4);
    padding-left: 12px;
  }
  .ee-tree-section-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.4px;
    color: #7AB8D4;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .ee-tree-item {
    display: flex; align-items: center; gap: 8px;
    padding: 4px 0;
    color: #d8d8dc;
    font-size: 13px;
  }
  .ee-tree-item.seed { color: #f0efe9; font-weight: 500; }
  .ee-tree-bullet {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #7AB8D4;
    box-shadow: 0 0 6px rgba(122,184,212,0.6);
    flex-shrink: 0;
  }
  .ee-tree-item.seed .ee-tree-bullet {
    width: 8px; height: 8px;
    background: #b8dbed;
    box-shadow: 0 0 10px rgba(122,184,212,0.9);
  }
  .ee-tree-item-text { flex: 1; }
  .ee-tree-seed-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: #7AB8D4;
    text-transform: uppercase;
    background: rgba(122,184,212,0.12);
    border: 1px solid rgba(122,184,212,0.35);
    padding: 1px 6px;
    border-radius: 4px;
  }
  .ee-link-picker {
    position: absolute;
    top: 100%; left: 0; right: 0;
    margin-top: 4px;
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.4);
    border-radius: 10px;
    box-shadow: 0 12px 30px rgba(0,0,0,0.6);
    z-index: 30;
    max-height: 320px;
    display: flex; flex-direction: column;
  }
  .ee-link-picker-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    color: #d8d8dc;
    font-size: 12px;
  }
  .ee-link-picker-header strong { color: #d4af37; font-weight: 500; }
  .ee-link-picker-close {
    background: rgba(255,255,255,0.06);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 22px; height: 22px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    padding: 0;
  }
  .ee-link-picker-empty {
    padding: 14px;
    color: #9aa0a6;
    font-size: 12px;
    text-align: center;
    margin: 0;
  }
  .ee-link-picker-body {
    overflow-y: auto;
    padding: 6px;
  }
  .ee-link-picker-group { margin-bottom: 6px; }
  .ee-link-picker-group-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.4px;
    color: #9aa0a6;
    text-transform: uppercase;
    padding: 6px 8px 4px;
  }
  .ee-link-picker-item {
    display: flex; align-items: center; gap: 8px;
    width: 100%;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    color: #d8d8dc;
    padding: 7px 10px;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    font-size: 12px;
    margin-bottom: 3px;
  }
  .ee-link-picker-item:hover {
    background: rgba(212,175,55,0.1);
    border-color: rgba(212,175,55,0.3);
  }
  .ee-link-picker-item.already-linked {
    background: rgba(212,175,55,0.12);
    border-color: rgba(212,175,55,0.4);
  }
  .ee-link-picker-mark {
    display: inline-flex; align-items: center; justify-content: center;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(212,175,55,0.15);
    color: #d4af37;
    font-size: 11px;
    flex-shrink: 0;
  }
  .ee-link-picker-item.already-linked .ee-link-picker-mark {
    background: #d4af37;
    color: #0a0a0c;
  }
  .ee-link-picker-text {
    flex: 1;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .ee-info-btn {
    background: rgba(122,184,212,0.18);
    color: #7AB8D4;
    border: 1px solid rgba(122,184,212,0.5);
    width: 28px; height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    font-style: italic;
    font-family: 'Georgia', serif;
    line-height: 1;
    padding: 0;
    margin-right: 8px;
    animation: ee-info-glow 2.4s ease-in-out infinite;
  }
  .ee-info-btn:hover {
    background: rgba(122,184,212,0.28);
    color: #b8dbed;
    border-color: rgba(122,184,212,0.7);
  }
  @keyframes ee-info-glow {
    0%, 100% { box-shadow: 0 0 10px rgba(122,184,212,0.4), inset 0 0 4px rgba(122,184,212,0.2); }
    50% { box-shadow: 0 0 18px rgba(122,184,212,0.7), inset 0 0 6px rgba(122,184,212,0.35); }
  }
  .ee-info-popup {
    background: rgba(212,175,55,0.06);
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 16px;
  }
  .ee-info-popup h4 {
    margin: 0 0 8px;
    font-family: 'DM Serif Display', serif;
    font-size: 16px;
    color: #f0efe9;
  }
  .ee-info-popup ul {
    margin: 0 0 10px;
    padding-left: 18px;
    color: #d8d8dc;
    font-size: 12px;
    line-height: 1.7;
  }
  .ee-info-popup ul strong {
    color: #d4af37;
    font-weight: 600;
  }
  .ee-info-tip {
    margin: 0 0 10px;
    color: #9aa0a6;
    font-size: 11px;
    font-style: italic;
  }
  .ee-info-close {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 11px;
  }
</style>
